import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { IActivity, IDropdown } from "../../models/BaseModel";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { Fetch } from "../../shared/Fetch";
import { JVInput } from "../../shared/JVInput";
import { JVCheckbox } from "../../shared/JVCheckbox";
import { JVTextArea } from "../../shared/JVTextarea";
import { JVDropdown } from "../../shared/JVDropdown";
import { JVDropdownInput } from "../../shared/JVDropdownInput";
import { InputText, InputTextProps } from "primereact/inputtext";

const schema = yup.object({
    name: yup.string().trim().required("Activity Name is required"),
    activityTypeId: yup.number().nullable(),
    desc: yup.string().nullable()
});

interface Iprops {
    singleData: IActivity,
    open: boolean,
    hideDialog: () => void,
    toast: any,
    reloadData: () => void,
    hasPermission: (pageName: string, featureName?: string) => boolean
}

interface IParamDrop extends IDropdown {
    type: string,
    isRequired: boolean,
    value?: string
}

export const Form = (props: Iprops) => {
    const [submitting, setSubmitting] = useState(false);
    const [cities, setCities] = useState<IDropdown[]>();
    const [activityTypes, setActivityTypes] = useState<IDropdown[]>();
    const [params, setParams] = useState<IParamDrop[]>();

    const { control, handleSubmit, formState: { errors }, reset, watch } = useForm<IActivity>({
        resolver: yupResolver(schema),
        defaultValues: {},
        shouldFocusError: false,
    });

    const activityTypeId = watch('activityTypeId');

    useEffect(() => {
        reset(props.singleData);
        if (props.singleData.id && props.singleData.activityTypeId)
            loadParametersValueOfActivity(props.singleData.id);
    }, [props.singleData]);

    useEffect(() => {
        if (!props.singleData.id) {
            if (activityTypeId) loadParametersOfActivityType(activityTypeId);
            else setParams(undefined);
        }
    }, [activityTypeId]);

    async function onCitySearch(text: string) {
        const response = await Fetch(`city/search`, "GET", null, `text=${text}`, null);
        if (response) {
            setCities(await response.json());
        }
    }

    const loadActivityTypes = async () => {
        const response = await Fetch(`activityType/dropdown`, "GET", null, null, null);
        if (response) {
            const d = await response.json();
            d.unshift({ id: 0, name: 'N/A' });
            setActivityTypes(d);
        }
    }

    const loadParametersOfActivityType = async (activityTypeId: number) => {
        setSubmitting(true);
        const response = await Fetch(`ParameterMast/poat/${activityTypeId}`, "GET", null, `forRequirement=false`, null);
        if (response) {
            const d: IParamDrop[] = await response.json();
            setParams(d);
        }
        setSubmitting(false);
    }
    const loadParametersValueOfActivity = async (activityId: number) => {
        setSubmitting(true);
        const response = await Fetch(`ParameterMast/pvoa/${activityId}`, "GET", null, null, null);
        if (response) {
            const d: IParamDrop[] = await response.json();
            setParams(d);
        }
        setSubmitting(false);
    }

    useEffect(() => { loadActivityTypes(); }, []);

    const saveToDB = async (body: IActivity) => {
        setSubmitting(true);
        const method = props.singleData.id ? "PUT" : "POST";
        const message = props.singleData.id ? "Updated Successfully" : "Added Successfully";

        body.cityId = body.city?.id;
        if (activityTypeId === 0) body.activityTypeId = undefined;

        body.paramsValue = params?.map(p => ({ paramId: p.id, value: p.value ?? '' }));

        const response = await Fetch(`activity${props.singleData.id ? '/' + props.singleData.id : ''}`, method, body, null, props.toast);
        if (response) {
            props.toast.current.show({ severity: 'success', detail: message, life: 3000 });
            props.reloadData();
            onHide();
        }
        setSubmitting(false);
    }

    const onHide = () => {
        props.hideDialog();
    }

    function onSubmit(data: IActivity) {
        let error = false;
        params?.forEach(p => {
            if (p.isRequired && p.type === 'string' && (!p.value || p.value.trim() === '')) {
                props.toast.current.show({ severity: 'error', detail: `Please fill the required field: ${p.name}`, life: 3000 });
                error = true;
            } else if (p.type === 'number' && p.isRequired && (!p.value || p.value.trim() === '')) {
                props.toast.current.show({ severity: 'error', detail: `Please fill the required field: ${p.name}`, life: 3000 });
                error = true;
            } else if (p.type === 'number' && ((Number(p.value)) < 0)) {
                props.toast.current.show({ severity: 'error', detail: `Can't be negative, the field: ${p.name}`, life: 3000 });
                error = true;
            }
        });
        if (error) return;
        saveToDB(data);
    }

    function onParamValueChange(paramId: number, value: string) {
        const updatedParams = params?.map(p => {
            if (p.id === paramId) {
                return { ...p, value: value };
            }
            return p;
        });
        setParams(updatedParams);
    }

    return (
        <Dialog visible={props.open} style={{ width: '80vw', maxWidth: '600px' }} header="Add/Edit Activity" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
                <JVInput required label="Activity Name" control={control} name="name" error={errors.name?.message} />
                <JVDropdownInput disabled={props.singleData.id ? true : false} label="Destination" placeholder="Search City" name="city" options={cities} control={control} optionLabel="name" completeMethod={(e) => onCitySearch(e.query)} error={errors.city?.id?.message} />
                <JVDropdown disabled={submitting || (props.singleData.id ? true : false)} label="ActivityType" options={activityTypes} optionValue="id" optionLabel="name" control={control} name="activityTypeId" error={errors.activityTypeId?.message} />
                {params?.map(p =>
                    <div id={p.name} className='field col-12 md:col-6'>
                        <label htmlFor={p.name}>{p.name}{p.isRequired && <span style={{ color: 'red' }}>*</span>}</label>
                        <InputText value={p.value} type={p.type === 'number' ? 'number' : "text"} onChange={e => { onParamValueChange(p.id, e.target.value) }} />
                    </div>
                )}
                <JVTextArea column={1} label="Description" control={control} name="desc" error={errors.desc?.message} />
                {props.singleData.id && <JVCheckbox column={1} label="Status" control={control} name="isActive" />}

                <div className="col-12 md:col-12 flex gap-4 justify-content-end mt-2">
                    <Button style={{ width: '100px' }} loading={submitting} label="SAVE" type="submit" className="p-button-rounded"></Button>
                    <Button style={{ width: '100px' }} label="CLOSE" onClick={onHide} type="button" className="p-button-rounded p-button-outlined surface-200"></Button>
                </div>
            </form>
        </Dialog>
    );
}
